import React, { useEffect, useMemo, useState } from "react";
import { VideoMetadata } from "../../types/media";
import PostVideoPlayer from "./PostVideoPlayer";
import PostVideoProcessingPlaceholder from "./PostVideoProcessingPlaceholder";

interface PostVideoViewProps {
    src: string;
    metadata?: VideoMetadata;
    thumbnail?: string;
    isProcessing?: boolean;
    autoPlay?: boolean;
    className?: string;
    onClick?: () => void;
}

const FALLBACK_RATIO = 16 / 9;
const PROBE_TIMEOUT_MS = 4000;

const PostVideoView: React.FC<PostVideoViewProps> = ({
    src,
    metadata,
    thumbnail,
    isProcessing,
    autoPlay,
    className,
    onClick,
}) => {
    const initialRatio = useMemo(() => {
        if (metadata?.width && metadata?.height && metadata.height > 0) {
            return metadata.width / metadata.height;
        }
        return null;
    }, [metadata?.width, metadata?.height]);

    const [aspectRatio, setAspectRatio] = useState<number | null>(initialRatio);

    useEffect(() => {
        if (aspectRatio !== null) return;

        // Fast path: thumbnail image (a small single-shot GET that we can use
        // for both processing and playable states since its aspect matches the
        // source video).
        if (thumbnail) {
            let cancelled = false;
            const img = new Image();
            img.onload = () => {
                if (cancelled) return;
                if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                    setAspectRatio(img.naturalWidth / img.naturalHeight);
                } else {
                    setAspectRatio(FALLBACK_RATIO);
                }
            };
            img.onerror = () => {
                if (cancelled) return;
                setAspectRatio(FALLBACK_RATIO);
            };
            img.src = thumbnail;
            return () => {
                cancelled = true;
            };
        }

        // No thumbnail and still processing: nothing we can probe yet.
        // Fall through to the placeholder at the default aspect so the user
        // sees the processing overlay instead of an indefinite skeleton.
        if (isProcessing) {
            setAspectRatio(FALLBACK_RATIO);
            return;
        }

        // Non-processing without a thumbnail: probe the video's metadata.
        if (!src) {
            setAspectRatio(FALLBACK_RATIO);
            return;
        }

        let cancelled = false;
        let resolved = false;
        const probe = document.createElement("video");
        probe.preload = "metadata";
        probe.muted = true;

        const resolve = (ratio: number): void => {
            if (cancelled || resolved) return;
            resolved = true;
            setAspectRatio(ratio);
        };

        probe.onloadedmetadata = () => {
            if (probe.videoWidth > 0 && probe.videoHeight > 0) {
                resolve(probe.videoWidth / probe.videoHeight);
            } else {
                resolve(FALLBACK_RATIO);
            }
        };
        probe.onerror = () => resolve(FALLBACK_RATIO);

        const fallbackTimer = setTimeout(() => resolve(FALLBACK_RATIO), PROBE_TIMEOUT_MS);

        probe.src = src;

        return () => {
            cancelled = true;
            clearTimeout(fallbackTimer);
            probe.removeAttribute("src");
            probe.load();
        };
    }, [thumbnail, src, isProcessing, aspectRatio]);

    if (aspectRatio === null) {
        return (
            <div
                className={`bg-gray-200 shimmer-bar overflow-hidden relative mx-auto ${className ?? ""}`}
                style={{ aspectRatio: FALLBACK_RATIO, width: "100%", maxWidth: "100%" }}
            />
        );
    }

    if (isProcessing) {
        return (
            <PostVideoProcessingPlaceholder
                thumbnail={thumbnail}
                aspectRatio={aspectRatio}
                className={className}
            />
        );
    }

    return (
        <PostVideoPlayer
            src={src}
            metadata={metadata}
            aspectRatio={aspectRatio}
            autoPlay={autoPlay}
            className={className}
            onClick={onClick}
        />
    );
};

export default PostVideoView;
